import Link from "next/link";
import parse from "html-react-parser";
import prettyBytes from "pretty-bytes";
import { cn } from "@/lib/utils";
import { DownloadProps, TermProps } from "@/lib/types";
import Text from "@/components/static/Text";
import Tags from "@/components/static/Tags";
import Tag from "@/components/static/Tag";
import Button from "@/components/static/Button";
import { useState } from "react";
import { AnimatePresence, motion, useScroll, useMotionValueEvent } from "framer-motion";
import DownloadIcon from "@/icons/DownloadIcon";
import Image from "next/image";

type ButtonLabels = {
    [key: string]: string;
};

export default function Download({ content }: { content: DownloadProps }) {
    const { description, downloads } = content.acf;
    const dummyFile = downloads.de ?? downloads.en ?? downloads.cz ?? downloads.cz;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const buttonLabels: ButtonLabels = {
        de: "Deutsch",
        en: "Englisch",
        cz: "Tschechisch",
        cn: "Chinesisch",
    };
    return (
        <>
            {downloads && (
                <div
                    className={cn("relative w-full p-8 bg-bgElement rounded-2xl flex items-center")}
                >
                    <div className='flex flex-wrap lg:flex-nowrap justify-center lg:flex-row items-center gap-x-8 lg:justify-between w-full bottom-0 left-0'>
                        <div className='max-w-full lg:max-w-[104px] w-full block h-full overflow-hidden bg-brand-light'>
                            {content._embedded?.["wp:featuredmedia"]?.[0] && (
                                <Image
                                    src={content._embedded["wp:featuredmedia"][0].source_url ?? ""}
                                    alt={content._embedded["wp:featuredmedia"][0].alt_text ?? ""}
                                    width={
                                        content._embedded["wp:featuredmedia"][0].media_details
                                            .width ?? ""
                                    }
                                    height={
                                        content._embedded["wp:featuredmedia"][0].media_details
                                            .height ?? ""
                                    }
                                    className={cn("w-full h-full object-fit")}
                                />
                            )}
                        </div>
                        <div className='mt-5 lg:mt-0 flex flex-col grow gap-4'>
                            <div className='flex items-center gap-4'>
                                {content._embedded?.["wp:term"] && (
                                    <Tags>
                                        {content._embedded["wp:term"]?.[0]?.map(
                                            (term: TermProps, i: number) => (
                                                <Tag key={i} label={term.name} />
                                            )
                                        )}
                                    </Tags>
                                )}
                                <span className='text-fade text-sm font-medium uppercase'>{`${prettyBytes(
                                    dummyFile.filesize
                                )} | ${dummyFile.subtype}`}</span>
                            </div>
                            <h3 className='text-h5 font-medium leading-snug'>
                                {content.title.rendered}
                            </h3>
                            {description && <Text className='max-w-lg'>{parse(description)}</Text>}
                        </div>
                        <button
                            onClick={() => setIsModalOpen((prev) => !prev)}
                            className='size-10 mt-5 lg:mt-0 p-2 rounded-full bg-brand-dark flex items-center justify-center shrink-0 group transition hover:scale-110'
                        >
                            <DownloadIcon className='' />
                        </button>
                    </div>
                    {isModalOpen && (
                        <AnimatePresence>
                            <motion.div
                                variants={{
                                    open: {
                                        visibility: "visible",
                                        opacity: "var(--opacity-open, 100%)",
                                    },
                                    close: {
                                        visibility: "hidden",
                                        opacity: "var(--opacity-close, 0)",
                                    },
                                }}
                                animate={isModalOpen ? "open" : "close"}
                                initial='close'
                                exit='close'
                                transition={{ duration: 0.3 }}
                                className='fixed flex items-center justify-center inset-0 size-full z-50'
                            >
                                <motion.div
                                    variants={{
                                        open: {
                                            visibility: "visible",
                                            opacity: "var(--opacity-open, 100%)",
                                            y: 0,
                                        },
                                        close: {
                                            visibility: "hidden",
                                            opacity: "var(--opacity-close, 0)",
                                            y: 10,
                                        },
                                    }}
                                    animate={isModalOpen ? "open" : "close"}
                                    initial='close'
                                    exit='close'
                                    transition={{ duration: 0.3 }}
                                    className='bg-brand-light p-6 z-20 rounded-xl w-full max-w-[16rem]'
                                >
                                    <div className='flex flex-col mb-6'>
                                        <span className='text-h6 text-white text-center'>
                                            {"Download"}
                                        </span>
                                        <span className='text-center text-white mt-2'>
                                            {content.title.rendered}
                                        </span>
                                    </div>
                                    <div className='flex flex-col gap-2'>
                                        {Object.entries(downloads).map(
                                            ([lang, download], index) =>
                                                download && (
                                                    <a
                                                        key={index}
                                                        className='bg-brand pt-[0.5rem] pb-[0.6rem] px-3 rounded-full text-white w-full flex items-center border justify-center hover:text-white hover:bg-primary transition'
                                                        href={download.url ?? ""}
                                                    >
                                                        {buttonLabels[lang]}
                                                    </a>
                                                )
                                        )}
                                        <Button
                                            as='button'
                                            type='white'
                                            className='rounded-full w-full flex items-center justify-center cursor-pointer transition'
                                            onClick={() => setIsModalOpen(false)}
                                        >
                                            {"Schließen"}
                                        </Button>
                                    </div>
                                </motion.div>
                                <div
                                    className='absolute inset-0 z-10 bg-on-primary/20'
                                    onClick={() => setIsModalOpen(false)}
                                ></div>
                            </motion.div>
                        </AnimatePresence>
                    )}
                </div>
            )}
        </>
    );
}
